import { Auth0Provider } from '@auth0/auth0-react';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import AxeptIoLoader from '@mint-lib/axeptIo';
import { InternalServerErrorPage } from '@mint-lib/error-boundary';
import i18n, { I18nextProvider, i18nInitPromise } from '@mint-lib/i18n';
import { Toaster } from '@mint-lib/notifications';
import { QueryClientProvider } from '@mint-lib/query-client';

import './theme-support.css';

import {
  AuthProvider,
  FlagsmithProvider,
  MintServiceLocator,
} from '@mint-lib/routing-context';
import getTheme, { GlobalStyles, ThemeProvider } from '@mint-lib/theme';
import {
  routerContext,
  routerInstance,
  RouterProvider,
} from '@mint/mint-iq-routing';
import { createRoot } from 'react-dom/client';

import { muteConsole } from './utils/muteConsole.js';

type ThemeModeType = 'dark' | 'light' | 'system';

const storedThemeValue = localStorage.getItem('theme') as ThemeModeType;
const isValidStoredThemeValue =
  storedThemeValue === 'light' ||
  storedThemeValue === 'dark' ||
  storedThemeValue === 'system';

// watch for auth events
routerContext.serviceLocator.getEventBus().on('auth', 'auth', () => {
  if (routerInstance.state.matches[1].routeId.startsWith('/_auth')) {
    routerInstance.invalidate().finally(() => {
      routerInstance.navigate({
        to:
          'next' in routerInstance.latestLocation.search
            ? decodeURIComponent(
                (routerInstance.latestLocation.search.next as string) ?? '/',
              )
            : '/',
      });
    });
  }
});

routerContext.serviceLocator.getEventBus().on('auth', 'logout', () => {
  globalThis.location.reload();
});

async function boot() {
  muteConsole();

  const credencials = await routerContext.getAuth0Credentials?.();
  const queryClient =
    await routerContext.serviceLocator.getOrThrowInstance('queryClient');
  const AppWrapper = () => {
    const [themeMode, setThemeMode] = useState<ThemeModeType>(
      isValidStoredThemeValue ? storedThemeValue : 'light',
    );

    // Get system theme preference
    const systemTheme = globalThis.matchMedia('(prefers-color-scheme: dark)')
      .matches
      ? 'dark'
      : 'light';

    useEffect(() => {
      document.documentElement.dataset.colorScheme =
        themeMode === 'system' ? systemTheme : themeMode;

      if (
        themeMode === 'dark' ||
        (themeMode === 'system' && systemTheme === 'dark')
      ) {
        document.body.classList.add('dark');
      } else {
        document.body.classList.remove('dark');
      }
    }, [themeMode, systemTheme]);

    useEffect(() => {
      const channel = new BroadcastChannel('themeChannel');
      channel.onmessage = (event) => {
        const themeValue = event.data as ThemeModeType;
        if (
          themeValue === 'light' ||
          themeValue === 'dark' ||
          themeValue === 'system'
        ) {
          setThemeMode(event.data);
        }
      };
      return () => {
        channel.close();
      };
    }, []);

    const theme = useMemo(
      () => ({
        ...getTheme(themeMode === 'system' ? systemTheme : themeMode),
      }),
      [themeMode, systemTheme],
    );

    useLayoutEffect(() => {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }, []);

    return (
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <MintServiceLocator.Provider value={routerContext.serviceLocator}>
            <FlagsmithProvider name="@mint/app">
              <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={theme}>
                  <GlobalStyles />
                  {credencials ? (
                    <AuthProvider>
                      <Auth0Provider
                        domain={credencials?.domain ?? ''}
                        clientId={credencials?.applicationClientId ?? ''}
                        authorizationParams={{
                          redirect_uri:
                            globalThis.location.origin + '/login-auth0',
                        }}
                      >
                        <RouterProvider
                          router={routerInstance}
                          context={routerContext}
                        />
                        <AxeptIoLoader />
                        <Toaster />
                      </Auth0Provider>
                    </AuthProvider>
                  ) : (
                    <InternalServerErrorPage />
                  )}
                </ThemeProvider>
              </QueryClientProvider>
            </FlagsmithProvider>
          </MintServiceLocator.Provider>
        </I18nextProvider>
      </React.StrictMode>
    );
  };
  // * Do not forget to update packages/shared/src/testing/render.tsx wrapper
  return i18nInitPromise.then(() =>
    createRoot(document.getElementById('root') as Element).render(
      <AppWrapper />,
    ),
  );
}
boot().catch((e) => {
  console.error(e);
});
